// Trees, rocks and ferns (RALLY_GOAL.md §11): card-based conifers built from a composited spruce
// branch texture (Poly Haven CC0 sprigs), three mesh LODs, camera-centred dynamic instancing
// (a handful of draw calls for ~37k trees), distance culling scaled by the quality preset, and a
// little wind sway.
import * as THREE from 'three/webgpu';
import { setRange } from './particles';
import { positionLocal, positionGeometry, positionWorld, normalWorld, normalViewGeometry, instanceIndex, hash, time, sin, vec3, float, texture, mix, smoothstep, abs, max, dot, uv, fwidth, log2, clamp, uniform, attribute, step , cameraPosition } from 'three/tsl';
import { MatteNodeMaterial, setEnvScale } from './matte';
import type { InstanceSet } from '../world/types';
import type { GameTextures } from './textures';

// ---------------------------------------------------------------------------------------------
// Geometry

/** The spruce card atlas holds a bark strip in its bottom 128 of 640 rows (v < 0.2). */
const BARK_V = 0.2;

interface Builder {
  pos: number[];
  nor: number[];
  uv: number[];
  col: number[];
  idx: number[];
}
const newBuilder = (): Builder => ({ pos: [], nor: [], uv: [], col: [], idx: [] });

function finish(trunk: Builder, crown: Builder): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  const nT = trunk.pos.length / 3;
  const pos = [...trunk.pos, ...crown.pos];
  const nor = [...trunk.nor, ...crown.nor];
  const uv = [...trunk.uv, ...crown.uv];
  const col = [...trunk.col, ...crown.col];
  const idx = [...trunk.idx, ...crown.idx.map((i) => i + nT)];
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}

function trunkMesh(b: Builder, height: number, r0: number, r1: number, sides: number): void {
  const rings = 4;
  const base = b.pos.length / 3;
  for (let j = 0; j <= rings; j++) {
    const t = j / rings;
    const y = t * height;
    const r = r0 + (r1 - r0) * Math.pow(t, 0.8);
    for (let k = 0; k <= sides; k++) {
      const a = (k / sides) * Math.PI * 2;
      const cx = Math.cos(a);
      const sz = Math.sin(a);
      b.pos.push(cx * r, y, sz * r);
      b.nor.push(cx, 0.15, sz);
      b.uv.push(y / height, (k / sides) * BARK_V);
      // Bark tint relative to the needle material colour (one shared material).
      const ao = 0.55 + 0.45 * t;
      b.col.push(ao * 1.03, ao * 0.63, ao * 1.18);
    }
  }
  for (let j = 0; j < rings; j++) {
    for (let k = 0; k < sides; k++) {
      const a = base + j * (sides + 1) + k;
      const c = a + sides + 1;
      b.idx.push(a, c, a + 1, a + 1, c, c + 1);
    }
  }
}

/** One branch card from the trunk outward: u along the branch, v across (texture 2:1). */
function card(b: Builder, y: number, az: number, len: number, droop: number, roll: number, crownMid: number, aoBase: number): void {
  const dx = Math.cos(az) * Math.cos(droop);
  const dy = -Math.sin(droop);
  const dz = Math.sin(az) * Math.cos(droop);
  // Side vector: horizontal perpendicular, rolled about the branch axis.
  const sx0 = -Math.sin(az);
  const sz0 = Math.cos(az);
  // up' = d × s0
  const ux = dy * sz0;
  const uy = dz * sx0 - dx * sz0;
  const uz = -dy * sx0;
  const cr = Math.cos(roll);
  const sr = Math.sin(roll);
  const sx = sx0 * cr + ux * sr;
  const sy = uy * sr;
  const sz = sz0 * cr + uz * sr;
  const w = len * 0.55;
  const base = b.pos.length / 3;
  const startOff = 0.12;
  for (const [u, v] of [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ]) {
    const along = startOff + u * len;
    const across = (v - 0.5) * w * (0.55 + 0.45 * u);
    const px = dx * along + sx * across;
    const py = y + dy * along + sy * across;
    const pz = dz * along + sz * across;
    b.pos.push(px, py, pz);
    // "Volume" normal: outward from the crown axis, biased up.
    let nx = px;
    let ny = (py - crownMid) * 0.35 + 0.6 * Math.hypot(px, pz);
    let nz = pz;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l;
    ny /= l;
    nz /= l;
    b.nor.push(nx, ny, nz);
    b.uv.push(u, BARK_V + (1 - BARK_V) * v);
    const ao = aoBase * (0.5 + 0.5 * u);
    b.col.push(ao, ao, ao);
  }
  b.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

export type TreeShape = 'spruce' | 'pine' | 'young';

/** Conifer at scale 1 (spruce 19 m, pine 22 m); lod 0 = full, 1 = reduced, 2 = far. */
export function conifer(shape: TreeShape, lod: 0 | 1 | 2): THREE.BufferGeometry {
  const H = shape === 'pine' ? 22 : 19;
  const trunk = newBuilder();
  const crown = newBuilder();
  trunkMesh(trunk, H * 0.98, shape === 'pine' ? 0.3 : 0.26, 0.03, lod === 0 ? 7 : lod === 1 ? 5 : 4);
  const whorls = lod === 0 ? 16 : lod === 1 ? 10 : 7;
  const perWhorl = lod === 0 ? 5 : 4;
  const t0 = shape === 'pine' ? 0.42 : shape === 'young' ? 0.03 : 0.08;
  const t1 = 0.96;
  const crownMid = H * (t0 + t1) * 0.5;
  let seed = shape === 'pine' ? 77 : shape === 'young' ? 31 : 11;
  const rnd = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  for (let i = 0; i < whorls; i++) {
    const t = t0 + ((t1 - t0) * (i + 0.5)) / whorls;
    const y = t * H;
    const u = (t - t0) / (t1 - t0);
    let len: number;
    let droop: number;
    if (shape === 'pine') {
      len = 2.4 * Math.pow(1 - u, 0.55) * Math.min(1, u * 3 + 0.45) + 0.4;
      droop = THREE.MathUtils.degToRad(8 - 22 * u);
    } else {
      len = (shape === 'young' ? 3.4 : 3.3) * Math.pow(1 - u, 0.85) + 0.3;
      droop = THREE.MathUtils.degToRad(14 + 18 * (1 - u));
    }
    // Fewer, bigger cards at lower LODs keep the silhouette.
    const lodGrow = lod === 0 ? 1 : lod === 1 ? 1.1 : 1.22;
    for (let k = 0; k < perWhorl; k++) {
      const az = i * 2.39996 + (k / perWhorl) * Math.PI * 2 + (rnd() - 0.5) * 0.5;
      const l = len * lodGrow * (0.85 + 0.3 * rnd());
      const roll = (k % 2 ? 1 : -1) * THREE.MathUtils.degToRad(25 + 25 * rnd());
      card(crown, y, az, l, droop + (rnd() - 0.5) * 0.15, roll, crownMid, 0.62 + 0.38 * u);
    }
    // One steeply rolled card per whorl between the others: the flat cards are slivers seen from
    // the side, which left open-ground trees looking like pipe cleaners.
    if (lod === 0) {
      const az = i * 2.39996 + Math.PI / perWhorl;
      card(crown, y, az, len * lodGrow * 0.85, droop * 0.7, (i % 2 ? 1 : -1) * THREE.MathUtils.degToRad(68 + 10 * rnd()), crownMid, 0.6 + 0.38 * u);
    }
  }
  // Dead lower branches on the bare trunk (dark, short, drooping) — plantation spruce/pine.
  if (lod === 0 && shape !== 'young') {
    const deadTop = t0 * H;
    for (let i = 0; i < 4; i++) {
      const y = deadTop * (0.35 + 0.16 * i);
      for (let k = 0; k < 3; k++) card(crown, y, i * 1.7 + k * 2.1, 0.7 + 0.35 * rnd(), 0.5, 0.6, crownMid, 0.22);
    }
  }
  // Leader at the top: two crossed upright cards.
  for (const az of [0, Math.PI / 2]) card(crown, H * 0.94, az, 1.6, -Math.PI / 2 + 0.05, Math.PI / 2, crownMid, 1);
  return finish(trunk, crown);
}

export function rockGeometry(detail: 0 | 1): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(1, detail ? 1 : 2);
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i);
    const y = p.getY(i);
    const z = p.getZ(i);
    const f = 0.82 + 0.3 * Math.sin(x * 3.1 + z * 1.7) * Math.cos(y * 2.3) + 0.08 * Math.sin(x * 9 + y * 7);
    p.setXYZ(i, x * f * 1.25, y * f * 0.7, z * f);
  }
  g.computeVertexNormals();
  g.deleteAttribute('uv');
  return g;
}

/** Fern clump: arching fronds from the fern atlas (512², five fronds). */
const FRONDS = [
  { x: 161, y: 13, w: 72, h: 378 },
  { x: 45, y: 57, w: 75, h: 429 },
  { x: 366, y: 56, w: 62, h: 402 },
  { x: 260, y: 14, w: 77, h: 242 },
  { x: 263, y: 262, w: 59, h: 232 },
];
export function fernGeometry(): THREE.BufferGeometry {
  const b = newBuilder();
  const N = 9;
  for (let k = 0; k < N; k++) {
    const f = FRONDS[k % FRONDS.length];
    const az = (k / N) * Math.PI * 2 + (k % 3) * 0.3;
    const len = 0.7 + 0.5 * ((k * 37) % 10) / 10;
    const width = len * (f.w / f.h) * 1.6;
    const ca = Math.cos(az);
    const sa = Math.sin(az);
    const base = b.pos.length / 3;
    const segs = 3;
    for (let s = 0; s <= segs; s++) {
      const t = s / segs;
      // Arch: rises then droops.
      const r = t * len * 0.85;
      const y = Math.sin(t * Math.PI * 0.75) * len * 0.55;
      for (const side of [-1, 1]) {
        const px = ca * r - sa * side * width * 0.5;
        const pz = sa * r + ca * side * width * 0.5;
        b.pos.push(px, y, pz);
        b.nor.push(ca * 0.3, 0.9, sa * 0.3);
        const u = (f.x + (side < 0 ? 0 : f.w)) / 512;
        const v = 1 - (f.y + f.h * (1 - t)) / 512;
        b.uv.push(u, v);
        const ao = 0.6 + 0.4 * t;
        b.col.push(ao, ao, ao);
      }
    }
    for (let s = 0; s < segs; s++) {
      const a = base + s * 2;
      b.idx.push(a, a + 1, a + 3, a, a + 3, a + 2);
    }
  }
  return finish(newBuilder(), b);
}

// ---------------------------------------------------------------------------------------------
// Materials

function swayNode(amount: number, heightScale: number) {
  const phase = hash(instanceIndex).mul(6.283);
  // Height up the tree from the geometry: positionLocal is already instanced (world) here.
  const h = positionGeometry.y.div(heightScale).clamp(0, 1);
  const s = sin(time.mul(1.1).add(phase)).mul(0.6).add(sin(time.mul(2.3).add(phase.mul(1.7))).mul(0.25));
  return positionLocal.add(vec3(s.mul(h.mul(h)).mul(amount), float(0), s.mul(h.mul(h)).mul(amount * 0.6)));
}

export function foliageMaterial(map: THREE.Texture, heightScale: number, sway: number): THREE.MeshStandardNodeMaterial {
  const m = new MatteNodeMaterial({ map, color: 0x8a9a64, alphaTest: 0.42, side: THREE.DoubleSide, roughness: 0.92, metalness: 0, vertexColors: true });
  // Needles are dark and self-shadowing: less sky than open ground (with the matte grazing
  // reflectance, the tops of the branches no longer read as snow).
  setEnvScale(m, 0.6);
  // Cut away needles within ~2.5 m of the camera (the alpha test does the cutting): a chase camera
  // pushed into a crown sees through it instead of filling the frame with branches. The shadow
  // pass's camera is the light, far away, so shadows keep every card.
  const near = smoothstep(float(0.9), float(2.6), positionWorld.distance(cameraPosition));
  m.opacityNode = texture(map, uv()).a.mul(near);
  m.normalNode = normalViewGeometry;
  if (sway > 0) m.positionNode = swayNode(sway, heightScale);
  return m;
}

/** Rock: triplanar rock texture with moss/needles settling on upward faces. */
export function rockMaterial(tx: GameTextures): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.9, metalness: 0 });
  const p = positionWorld;
  const n = abs(normalWorld);
  const w = n.mul(n).mul(n);
  const ws = w.x.add(w.y).add(w.z);
  const rs = tx.sets.rock.albedo;
  const rock = texture(rs, p.zy.div(2.2)).rgb.mul(w.x).add(texture(rs, p.xz.div(2.2)).rgb.mul(w.y)).add(texture(rs, p.xy.div(2.2)).rgb.mul(w.z)).div(ws);
  const moss = texture(tx.sets.forest_floor.albedo, p.xz.div(2.5)).rgb;
  const top = smoothstep(float(0.55), float(0.85), normalWorld.y);
  m.colorNode = mix(rock.mul(0.85), moss, top.mul(0.75));
  m.roughnessNode = max(float(0.75), dot(rock, vec3(0.2)).add(0.7));
  return m;
}

// ---------------------------------------------------------------------------------------------
// Dynamic instancing

export interface FoliageLod {
  geometry: THREE.BufferGeometry;
  /** Material(s) for this LOD if different from the kind's (e.g. the billboard). */
  material?: THREE.Material | THREE.Material[];
  /** Max distance (m) before the preset's distance scale. */
  maxDist: number;
  castShadow: boolean;
  /**
   * Far band drawn from one static buffer holding every instance; the vertex shader collapses the
   * ones outside the band. Must be the last LOD, must be cheap geometry (billboards), and its
   * material must be unique to this kind (it receives a positionNode).
   */
  static?: boolean;
}

interface Kind {
  lods: FoliageLod[];
  /** Dynamic LODs: two meshes each (ping-pong: the hidden one is rewritten, then they swap). */
  meshes: THREE.InstancedMesh[][];
  active: number;
  counts: number[];
  matrices: Float32Array;
  colors: Float32Array;
  x: Float32Array;
  z: Float32Array;
  /** Spatial grid: cell → instance indices. */
  cells: Map<number, Int32Array>;
  /** Static far band (if any) and its culling uniforms. */
  far: THREE.InstancedMesh | null;
  farUniforms: { ref: ReturnType<typeof uniform>; near2: ReturnType<typeof uniform>; far2: ReturnType<typeof uniform> } | null;
}

const CELL = 64;
const cellKey = (cx: number, cz: number) => (cx + 32768) * 65536 + (cz + 32768);

/**
 * Instances live in a 64 m grid. The near LODs are rebuilt every few metres of camera travel by
 * copying the visible instances into pre-sized buffers (two copies per LOD, alternated). The far
 * band is one static buffer culled in the vertex shader: large buffer uploads stall WebGPU in
 * Chrome (measured 60–110 ms per ~1 MB write on this machine), so the big set is uploaded once.
 */
export class FoliageSystem {
  readonly group = new THREE.Group();
  private kinds: Kind[] = [];
  private lastX = Infinity;
  private lastZ = Infinity;
  private lastScale = -1;
  /** Distance scale from the quality preset (1 = Medium). */
  distanceScale = 1;
  shadows = true;
  /** Camera positions the near-LOD buffers must fit (track samples), and the largest scale used. */
  probes: { x: number; z: number }[] = [];
  maxScale = 1.3;

  /** `lean`: max random tilt (rad) per instance. */
  add(set: InstanceSet, lods: FoliageLod[], materials: THREE.Material | THREE.Material[], tint: (i: number, out: THREE.Color) => void, lean = 0): void {
    const n = set.data.length / 5;
    if (!n) return;
    const matrices = new Float32Array(n * 16);
    const colors = new Float32Array(n * 3);
    const xs = new Float32Array(n);
    const zs = new Float32Array(n);
    const m4 = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const p = new THREE.Vector3();
    const s = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0);
    const tiltAxis = new THREE.Vector3();
    const tilt = new THREE.Quaternion();
    const c = new THREE.Color();
    const buckets = new Map<number, number[]>();
    for (let i = 0; i < n; i++) {
      const o = i * 5;
      p.set(set.data[o], set.data[o + 1], set.data[o + 2]);
      q.setFromAxisAngle(up, set.data[o + 3]);
      if (lean > 0) {
        const h1 = Math.sin(i * 91.7) * 4375.85;
        const h2 = Math.sin(i * 37.3) * 1597.13;
        const a = (h1 - Math.floor(h1)) * Math.PI * 2;
        tiltAxis.set(Math.cos(a), 0, Math.sin(a));
        tilt.setFromAxisAngle(tiltAxis, (h2 - Math.floor(h2)) * lean);
        q.premultiply(tilt);
      }
      const sc = set.data[o + 4];
      s.set(sc, sc, sc);
      m4.compose(p, q, s);
      m4.toArray(matrices, i * 16);
      tint(i, c);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
      xs[i] = p.x;
      zs[i] = p.z;
      const k = cellKey(Math.floor(p.x / CELL), Math.floor(p.z / CELL));
      let bkt = buckets.get(k);
      if (!bkt) buckets.set(k, (bkt = []));
      bkt.push(i);
    }
    const cells = new Map<number, Int32Array>();
    for (const [k, v] of buckets) cells.set(k, Int32Array.from(v));
    const staticLast = lods[lods.length - 1].static === true;
    const dyn = staticLast ? lods.slice(0, -1) : lods;
    const kind: Kind = { lods: dyn, meshes: [], active: 0, counts: dyn.map(() => 0), matrices, colors, x: xs, z: zs, cells, far: null, farUniforms: null };
    // Size each dynamic LOD for the worst camera position along the stage (+ margin); never grow
    // (three binds the original instance buffer, so a bigger one would not be used).
    const caps = this.measureCaps(kind);
    kind.meshes = dyn.map((l, li) =>
      [0, 1].map(() => {
        const cap = Math.max(16, Math.min(n, Math.ceil(caps[li] * 1.25) + 64));
        const im = new THREE.InstancedMesh(l.geometry, l.material ?? materials, cap);
        im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
        im.count = 0;
        im.frustumCulled = false;
        im.castShadow = l.castShadow;
        im.receiveShadow = true;
        im.visible = false;
        this.group.add(im);
        return im;
      }),
    );
    if (staticLast) {
      const l = lods[lods.length - 1];
      const geo = l.geometry.clone();
      const ipos = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) {
        ipos[i * 3] = xs[i];
        ipos[i * 3 + 1] = matrices[i * 16 + 13];
        ipos[i * 3 + 2] = zs[i];
      }
      geo.setAttribute('ipos', new THREE.InstancedBufferAttribute(ipos, 3));
      const mat = (l.material ?? materials) as THREE.NodeMaterial;
      const ref = uniform(new THREE.Vector2(1e9, 1e9));
      const near2 = uniform(0);
      const far2 = uniform(0);
      const ip = attribute('ipos', 'vec3');
      const d = ip.xz.sub(ref);
      const d2 = dot(d, d);
      // Collapse instances outside [near, far): degenerate triangles are culled by the rasterizer.
      const show = step(near2, d2).mul(step(d2, far2));
      mat.positionNode = positionLocal.mul(show);
      const im = new THREE.InstancedMesh(geo, mat, n);
      im.instanceMatrix.array.set(matrices);
      im.instanceColor = new THREE.InstancedBufferAttribute(colors.slice(), 3);
      im.frustumCulled = false;
      im.castShadow = false;
      im.receiveShadow = true;
      this.group.add(im);
      kind.far = im;
      kind.farUniforms = { ref, near2, far2 };
      kind.lods = lods;
    }
    this.kinds.push(kind);
  }

  /** Largest per-LOD instance count over the probe positions at the largest distance scale. */
  private measureCaps(kind: Kind): number[] {
    const dyn = kind.meshes.length ? kind.meshes.length : kind.counts.length;
    const lods = kind.lods.slice(0, dyn);
    const caps = lods.map(() => 0);
    const limits = lods.map((l) => (l.maxDist * this.maxScale) ** 2);
    const maxR = lods[lods.length - 1].maxDist * this.maxScale;
    const pts = this.probes.length ? this.probes : [{ x: 0, z: 0 }];
    const cnt = lods.map(() => 0);
    for (const pt of pts) {
      cnt.fill(0);
      this.forEachInRange(kind, pt.x, pt.z, maxR, (d2) => {
        for (let l = 0; l < lods.length; l++) {
          if (d2 < limits[l]) {
            cnt[l]++;
            return;
          }
        }
      });
      for (let l = 0; l < lods.length; l++) caps[l] = Math.max(caps[l], cnt[l]);
    }
    if (!this.probes.length) for (let l = 0; l < lods.length; l++) caps[l] = kind.x.length;
    return caps;
  }

  private forEachInRange(kind: Kind, x: number, z: number, r: number, fn: (d2: number, i: number) => void): void {
    const c0 = Math.floor((x - r) / CELL);
    const c1 = Math.floor((x + r) / CELL);
    const r0 = Math.floor((z - r) / CELL);
    const r1 = Math.floor((z + r) / CELL);
    for (let cx = c0; cx <= c1; cx++) {
      for (let cz = r0; cz <= r1; cz++) {
        const idx = kind.cells.get(cellKey(cx, cz));
        if (!idx) continue;
        for (let j = 0; j < idx.length; j++) {
          const i = idx[j];
          const dx = kind.x[i] - x;
          const dz = kind.z[i] - z;
          fn(dx * dx + dz * dz, i);
        }
      }
    }
  }

  private pending: Kind[] = [];

  /**
   * Re-bucket the near LODs around the camera after 12 m of travel. Kinds are rebuilt one per frame
   * so no single frame carries all the instance uploads (each upload is a small hitch in Chrome).
   */
  update(camX: number, camZ: number, force = false): void {
    const first = this.lastX === Infinity;
    const moved = Math.hypot(camX - this.lastX, camZ - this.lastZ);
    if (force || first || ((moved >= 12 || this.lastScale !== this.distanceScale) && this.pending.length === 0)) {
      this.lastX = camX;
      this.lastZ = camZ;
      this.lastScale = this.distanceScale;
      this.pending = [...this.kinds];
      if (force || first) {
        for (const k of this.pending) this.rebuild(k, camX, camZ);
        this.pending.length = 0;
        return;
      }
    }
    const k = this.pending.shift();
    if (k) this.rebuild(k, this.lastX, this.lastZ);
  }

  private rebuild(kind: Kind, camX: number, camZ: number): void {
    {
      const L = kind.meshes.length;
      const target = 1 - kind.active;
      const limits = kind.lods.slice(0, L).map((l) => (l.maxDist * this.distanceScale) ** 2);
      const maxR = kind.lods[L - 1].maxDist * this.distanceScale;
      for (let l = 0; l < L; l++) kind.counts[l] = 0;
      const caps = kind.meshes.map((m) => m[target].instanceMatrix.count);
      this.forEachInRange(kind, camX, camZ, maxR, (d2, i) => {
        for (let l = 0; l < L; l++) {
          if (d2 < limits[l]) {
            const k = kind.counts[l];
            if (k >= caps[l]) return;
            kind.counts[l] = k + 1;
            const im = kind.meshes[l][target];
            const dst = im.instanceMatrix.array as Float32Array;
            const src = kind.matrices;
            for (let e = 0, o = k * 16, i0 = i * 16; e < 16; e++) dst[o + e] = src[i0 + e];
            const ca = im.instanceColor!.array as Float32Array;
            ca[k * 3] = kind.colors[i * 3];
            ca[k * 3 + 1] = kind.colors[i * 3 + 1];
            ca[k * 3 + 2] = kind.colors[i * 3 + 2];
            return;
          }
        }
      });
      for (let l = 0; l < L; l++) {
        const im = kind.meshes[l][target];
        kind.meshes[l][kind.active].visible = false;
        im.visible = true;
        im.count = kind.counts[l];
        im.castShadow = this.shadows && kind.lods[l].castShadow;
        setRange(im.instanceMatrix, im.count * 16);
        setRange(im.instanceColor!, im.count * 3);
      }
      kind.active = target;
      if (kind.farUniforms) {
        const u = kind.farUniforms;
        (u.ref.value as THREE.Vector2).set(camX, camZ);
        u.near2.value = maxR * maxR;
        const farR = kind.lods[kind.lods.length - 1].maxDist * this.distanceScale;
        u.far2.value = farR * farR;
      }
    }
  }

  get visibleCount(): number {
    let n = 0;
    for (const k of this.kinds) for (const c of k.counts) n += c;
    return n;
  }
}

// ---------------------------------------------------------------------------------------------
// Far LOD: two crossed quads showing the full tree rendered once at startup.

export interface Billboard {
  shape: TreeShape;
  target: THREE.RenderTarget;
  geometry: THREE.BufferGeometry;
  material: THREE.MeshStandardNodeMaterial;
  height: number;
  radius: number;
}

export function makeBillboard(shape: TreeShape): Billboard {
  const H = shape === 'pine' ? 22 : 19;
  const R = 3.9;
  // Half float: the baked colours are linear albedo, which 8-bit storage would crush.
  const target = new THREE.RenderTarget(256, 512, { type: THREE.HalfFloatType, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter });
  const pos: number[] = [];
  const nor: number[] = [];
  const uvs: number[] = [];
  const idx: number[] = [];
  for (const a of [0, Math.PI / 2]) {
    const cx = Math.cos(a);
    const cz = Math.sin(a);
    const b = pos.length / 3;
    for (const [u, v] of [
      [0, 0],
      [1, 0],
      [1, 1],
      [0, 1],
    ]) {
      const s = u * 2 - 1;
      pos.push(cx * s * R, v * H, cz * s * R);
      // Volume normal: outward at the edges, up at the top.
      const nx = cx * s * 0.8;
      const ny = 0.35 + 0.5 * v;
      const nz = cz * s * 0.8;
      const l = Math.hypot(nx, ny, nz);
      nor.push(nx / l, ny / l, nz / l);
      uvs.push(u, v);
    }
    idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(idx);
  geometry.computeBoundingSphere();
  const material = new MatteNodeMaterial({ map: target.texture, alphaTest: 0.5, side: THREE.DoubleSide, roughness: 0.95, metalness: 0 });
  // Mipmapping averages the sparse cut-out alpha down, eroding distant trees into sticks: boost the
  // sampled alpha with the mip level actually used (coverage stays close to the full-res silhouette).
  const lod = log2(max(fwidth(uv().x).mul(256), fwidth(uv().y).mul(512)).max(1e-4));
  material.opacityNode = texture(target.texture, uv()).a.mul(float(1).add(clamp(lod, 0, 6).mul(0.45)));
  material.normalNode = normalViewGeometry;
  setEnvScale(material, 1.0);
  return { shape, target, geometry, material, height: H, radius: R };
}

/** Render each tree's full-detail mesh into its billboard texture (flat ambient → albedo × AO). */
export function bakeBillboards(renderer: THREE.WebGPURenderer, boards: Billboard[], material: THREE.Material): void {
  const scene = new THREE.Scene();
  scene.add(new THREE.AmbientLight(0xffffff, Math.PI));
  const prevTarget = renderer.getRenderTarget();
  const prevClear = new THREE.Color();
  renderer.getClearColor(prevClear);
  const prevAlpha = renderer.getClearAlpha();
  renderer.setClearColor(0x000000, 0);
  for (const b of boards) {
    const mesh = new THREE.Mesh(conifer(b.shape, 0), material);
    scene.add(mesh);
    const cam = new THREE.OrthographicCamera(-b.radius, b.radius, b.height, 0, 0.1, 50);
    cam.position.set(0, 0, 20);
    cam.lookAt(0, 0, 0);
    renderer.setRenderTarget(b.target);
    renderer.clear();
    renderer.render(scene, cam);
    scene.remove(mesh);
    mesh.geometry.dispose();
  }
  renderer.setRenderTarget(prevTarget);
  renderer.setClearColor(prevClear, prevAlpha);
}
